/**
 * Public-key envelope decryption (ECIES-style: ephemeral X25519 + HKDF-SHA256
 * + AES-256-GCM), mirroring `zunox_crypto::org_envelope` byte-for-byte. The
 * organization's private key is used here for a single `getSharedSecret`
 * call and then discarded; it never leaves the browser, and this page never
 * sends it anywhere.
 */
import { x25519 } from '@noble/curves/ed25519';
import { hkdf } from '@noble/hashes/hkdf';
import { sha256 } from '@noble/hashes/sha256';
import { base64ToBytes, bytesToBase64 } from '~/util/bytes';
import { encryptionKeyOf } from '~/util/orgSigning';
import { normalizeActivityPayload } from '~/util/intervalPayload';
const ALGORITHM = 'X25519-HKDF-SHA256-AES-256-GCM';
const HKDF_INFO = new TextEncoder().encode('zunox-hub/org-summary-envelope/v1');
export class DecryptionError extends Error {
}
/** Generates a fresh organization keypair. Only the public half is ever safe
 * to send anywhere — the private half is this function's entire output that
 * matters; the caller is responsible for showing it to the admin exactly
 * once and never persisting it anywhere but the admin's own storage. */
export function generateOrgKeyPair() {
    const privateKey = x25519.utils.randomSecretKey();
    const publicKey = x25519.getPublicKey(privateKey);
    return {
        publicKeyBase64: bytesToBase64(publicKey),
        privateKeyBase64: bytesToBase64(privateKey),
    };
}
async function deriveAesKey(sharedSecret, ephemeralPublicKey, usage) {
    const keyBytes = hkdf(sha256, sharedSecret, ephemeralPublicKey, HKDF_INFO, 32);
    return crypto.subtle.importKey('raw', keyBytes, 'AES-GCM', false, [usage]);
}
/** The public half of a pasted organization key (either format). Lets the
 * admin console confirm a pasted key really belongs to a given key version
 * before using it. */
export function publicKeyFromPrivate(organizationSecret) {
    return bytesToBase64(x25519.getPublicKey(encryptionKeyOf(organizationSecret)));
}
/**
 * Encrypts `plaintext` to an organization public key, byte-for-byte the same
 * construction as `zunox_crypto::org_envelope::encrypt`: a fresh ephemeral
 * X25519 key and random nonce per call. Used to re-encrypt stored history
 * onto a new key after a rotation.
 */
export async function encryptOrgSummary(publicKeyBase64, keyVersion, plaintext) {
    const ephemeralPrivate = x25519.utils.randomSecretKey();
    const ephemeralPublic = x25519.getPublicKey(ephemeralPrivate);
    const sharedSecret = x25519.getSharedSecret(ephemeralPrivate, base64ToBytes(publicKeyBase64));
    const key = await deriveAesKey(sharedSecret, ephemeralPublic, 'encrypt');
    const nonce = crypto.getRandomValues(new Uint8Array(12));
    const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce }, key, plaintext);
    return {
        algorithm: ALGORITHM,
        key_version: keyVersion,
        ephemeral_pubkey: bytesToBase64(ephemeralPublic),
        nonce: bytesToBase64(nonce),
        ciphertext: bytesToBase64(new Uint8Array(ciphertext)),
    };
}
/**
 * Decrypts one envelope to its plaintext bytes with a pasted organization
 * key (the combined `zxk2.` form or a plain key from before signing). Fails
 * closed: a wrong key and a tampered envelope are indistinguishable to the
 * caller, same as `zunox_crypto::org_envelope::decrypt`.
 */
export async function decryptOrgSummary(organizationSecret, record) {
    if (record.algorithm !== ALGORITHM) {
        throw new DecryptionError(`Unsupported envelope algorithm (${record.algorithm}) — this build only understands ${ALGORITHM}.`);
    }
    try {
        const privateKey = encryptionKeyOf(organizationSecret);
        const ephemeralPublicKey = base64ToBytes(record.ephemeral_pubkey);
        const sharedSecret = x25519.getSharedSecret(privateKey, ephemeralPublicKey);
        const key = await deriveAesKey(sharedSecret, ephemeralPublicKey, 'decrypt');
        const plaintext = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: base64ToBytes(record.nonce) }, key, base64ToBytes(record.ciphertext));
        return new Uint8Array(plaintext);
    }
    catch (err) {
        if (err instanceof DecryptionError)
            throw err;
        throw new DecryptionError('Could not decrypt: the private key is wrong, or this data was corrupted in transit.');
    }
}
/** Decrypts and parses one summary as the activity payload `zunox-agent`
 * sends, either the interval-contract form or the older `activity` form. */
export async function decryptOrgActivitySummary(privateKeyBase64, record) {
    return parseActivityPayload(await decryptOrgSummary(privateKeyBase64, record));
}
/** Inflates gzip bytes with the platform's own decompressor. */
export async function gunzip(bytes) {
    const stream = new Blob([bytes])
        .stream()
        .pipeThrough(new DecompressionStream('gzip'));
    return new Uint8Array(await new Response(stream).arrayBuffer());
}
/** Decrypts one sealed day: the same payload a summary carries, gzipped by
 * the agent before encryption. */
export async function decryptOrgDayBundle(privateKeyBase64, record) {
    const sealed = await decryptOrgSummary(privateKeyBase64, record);
    let bytes;
    try {
        bytes = await gunzip(sealed);
    }
    catch {
        throw new DecryptionError('Decrypted successfully, but the sealed day was not valid gzip.');
    }
    return parseActivityPayload(bytes);
}
export function parseActivityPayload(bytes) {
    const text = new TextDecoder().decode(bytes);
    let parsed;
    try {
        parsed = JSON.parse(text);
    }
    catch {
        throw new DecryptionError('Decrypted successfully, but the payload was not valid JSON.');
    }
    return normalizeActivityPayload(parsed);
}
